'use client';

import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { useState, useEffect, HTMLAttributeAnchorTarget, ReactNode, AnchorHTMLAttributes } from 'react';
import * as React from 'react';
import { UrlObject } from 'url';

interface DynamicLinkAttributes extends AnchorHTMLAttributes<HTMLAnchorElement> {
    target?: HTMLAttributeAnchorTarget;
    rel?: string;
    'aria-label'?: string;
}

export interface DynamicLinkProps extends Omit<NextLinkProps, 'href'>, Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'onClick' | 'onMouseEnter' | 'onMouseLeave' | 'id' | 'style' | 'title'> {
    href: string | UrlObject;
    children: ReactNode;
    ariaLabelExternal?: string;
}

const getDynamicLinkAttributes = (hrefInput: string | UrlObject, ariaLabelExternal?: string): DynamicLinkAttributes => {
    const internalHostname = 'cardanofoundation.org';
    const defaultExternalAriaLabel = 'Opens in a new tab';

    let hrefString: string | undefined;
    let inputIsLikelyFullUrl = false;

    if (typeof hrefInput === 'string') {
        hrefString = hrefInput;
        inputIsLikelyFullUrl = hrefString.startsWith('http://') || hrefString.startsWith('https://');
    } else if (hrefInput && typeof hrefInput.pathname === 'string') {
        hrefString = hrefInput.href || hrefInput.pathname || '';
        inputIsLikelyFullUrl = !!(hrefInput.protocol || hrefInput.host || hrefInput.hostname);
    }

    if (!hrefString) {
        return {};
    }

    const specialProtocols = ['mailto:', 'tel:', 'sms:', 'blob:', 'ftp:', 'file:'];
    if (specialProtocols.some(protocol => hrefString!.toLowerCase().startsWith(protocol))) {
        return {};
    }

    if (hrefString.startsWith('#') || (hrefString.startsWith('/') && !hrefString.startsWith('//'))) {
        return {};
    }

    try {
        const base = typeof window !== 'undefined' ? window.location.origin : `https://${internalHostname}`;
        const url = new URL(hrefString, inputIsLikelyFullUrl ? undefined : base);

        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return {};
        }

        if (url.hostname && url.hostname !== internalHostname && !url.hostname.endsWith('.' + internalHostname)) {
            return {
                target: '_blank',
                rel: 'noopener noreferrer',
                'aria-label': ariaLabelExternal || `Opens ${url.hostname} in a new tab` || defaultExternalAriaLabel,
            };
        }
    } catch {
        if (hrefString.startsWith('//')) {
            try {
                const tempUrl = new URL(`https:${hrefString}`);
                if (tempUrl.hostname && tempUrl.hostname !== internalHostname && !tempUrl.hostname.endsWith('.' + internalHostname)) {
                    return {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        'aria-label': ariaLabelExternal || `Opens ${tempUrl.hostname} in a new tab` || defaultExternalAriaLabel,
                    };
                }
            } catch {
                return {};
            }
        }
        return {};
    }

    return {};
};

export const DynamicLink = React.forwardRef<HTMLAnchorElement, DynamicLinkProps>((
    { href, children, className, ariaLabelExternal, ...rest },
    ref
) => {
    const [attributes, setAttributes] = useState<DynamicLinkAttributes>({});

    useEffect(() => {
        setAttributes(getDynamicLinkAttributes(href, ariaLabelExternal));
    }, [href, ariaLabelExternal]);

    return (
        <NextLink
            href={href}
            className={className}
            {...rest}
            {...attributes}
            ref={ref}
        >
            {children}
        </NextLink>
    );
});

DynamicLink.displayName = 'DynamicLink';